/**
 * @file IntelLayoutHeading.tsx
 * @brief U4: a Meeting Intelligence Intel / Points heading that collapses
 *        and drags (U5: like the phone's Home).
 *
 * @details
 * Same heading as before (pill, dot, line); the ONLY addition is the arrow at
 * the end of the line, on the same centre line, 8 px after it (GridHeading's
 * action gap): down = open, right = closed, the dot/line's muted grey. When
 * closed, the section's count (where it has one) sits just before the arrow.
 * Mockup: scratch/dispatcher/section-chevron-mockup.html (DES 03-11-2026).
 * - Click (or Enter / Space) = collapse / expand.
 * - Drag the heading itself (grab cursor): the section lifts and the others
 *   slide out of its way, as a Home section does on the phone
 *   (../useSectionSort.ts, U5 03-11-2026).
 * - Alt+↑ / Alt+↓ on a focused heading moves it (the keyboard's way to drag).
 * - No right-click menu since U5: web Home has none, and "Reset layout" is in
 *   the meeting's ••• menu.
 * No handles or other controls. Layout rules: ../intelLayout.ts.
 *
 * @author DES
 * @date 03-11-2026
 * @updated 03-11-2026 — U5: phone-Home drag, right-click menu removed
 */

import { type KeyboardEvent, type ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { GridHeading } from "@/components/ui/GridHeading";
import { cn } from "@/shared/theme/cn";
import { MEETING_GRID_HEADINGS, MEETING_OLD_HEADING_CLS } from "./MeetingBlockHeading";
import type { TabLayoutControl } from "../useIntelLayout";

/** The mockup's arrow grey (#8d8985), the same in light and dark. */
const CHEVRON_CLS = "text-[#8d8985]";

/** The count (closed only) and the arrow, for a heading line's far end. */
export function SectionChevron({ collapsed, count }: { collapsed: boolean; count?: number }) {
  const Icon = collapsed ? ChevronRight : ChevronDown;
  return (
    <>
      {collapsed && count ? (
        <span className="text-sm font-semibold tabular-nums text-app-text-secondary">{count}</span>
      ) : null}
      <span aria-hidden className="flex h-[22px] w-[22px] items-center justify-center">
        <Icon className={cn("h-[18px] w-[18px]", CHEVRON_CLS)} strokeWidth={2.2} />
      </span>
    </>
  );
}

/**
 * The behaviour around a heading row: click to collapse (when `collapsible`)
 * and drag. `children` is the row as it looked before.
 */
export function IntelLayoutFrame({
  ctl,
  sectionKey,
  label,
  collapsible = true,
  className,
  children,
}: {
  ctl: TabLayoutControl;
  sectionKey: string;
  /** For screen readers: the heading's text. */
  label: string;
  /** false = drags, but a click does nothing (the notes card's header). */
  collapsible?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const collapsed = ctl.isCollapsed(sectionKey);
  const sort = ctl.sort;
  const movable = Boolean(sort) && ctl.units.length > 1;

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
      const dir = e.key === "ArrowUp" ? "up" : "down";
      if (ctl.canMove(sectionKey, dir)) ctl.move(sectionKey, dir);
      return;
    }
    if (collapsible && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      ctl.toggle(sectionKey);
    }
  };

  return (
    <div
      role={collapsible ? "button" : undefined}
      tabIndex={collapsible || movable ? 0 : undefined}
      aria-expanded={collapsible ? !collapsed : undefined}
      aria-label={collapsible ? label : undefined}
      aria-keyshortcuts={movable ? "Alt+ArrowUp Alt+ArrowDown" : undefined}
      onClick={collapsible ? () => ctl.toggle(sectionKey) : undefined}
      onKeyDown={onKeyDown}
      onPointerDown={movable ? (e) => sort?.onPointerDown(sectionKey, e) : undefined}
      // The browser's own drag of text / images would fight ours.
      onDragStart={(e) => e.preventDefault()}
      className={cn(
        "relative select-none outline-none [-webkit-touch-callout:none]",
        movable ? "cursor-grab active:cursor-grabbing" : collapsible && "cursor-pointer",
        "focus-visible:rounded-full focus-visible:ring-2 focus-visible:ring-brand/40",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * The drawer heading (MeetingBlockHeading's look) with the arrow at the line's
 * end. `className` = the heading's margin when open, `collapsedClassName`
 * when closed (the room the section's body used to leave).
 */
export function IntelLayoutHeading({
  ctl,
  sectionKey,
  label,
  count,
  className = "mb-3",
  collapsedClassName,
  dir = "ltr",
}: {
  ctl: TabLayoutControl;
  sectionKey: string;
  label: string;
  /** Shown before the arrow while closed (sections that are a list). */
  count?: number;
  className?: string;
  collapsedClassName?: string;
  dir?: "ltr" | "rtl";
}) {
  const collapsed = ctl.isCollapsed(sectionKey);
  const margin = collapsed && collapsedClassName ? collapsedClassName : className;
  const chevron = <SectionChevron collapsed={collapsed} count={count} />;
  return (
    <IntelLayoutFrame ctl={ctl} sectionKey={sectionKey} label={label} className={margin}>
      {MEETING_GRID_HEADINGS ? (
        <GridHeading label={label} action={chevron} dir={dir} />
      ) : (
        <div dir={dir} className="flex items-center gap-2">
          <h3 className={cn("flex-1", MEETING_OLD_HEADING_CLS)}>{label}</h3>
          {chevron}
        </div>
      )}
    </IntelLayoutFrame>
  );
}

export default IntelLayoutHeading;
